import {Combined, Layer, Location, Transformable} from "../../../feature/project/Shot";
import {Button, Flex, Input, Modal, Space, Tabs, Typography} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {ShotState} from "./ShotViewport";
import {Status} from "../../../feature/project/status";
import {fetchTags} from "../../../feature/project/projectsSlice";
import {Tag} from "../../../feature/project/fields";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {useParams} from "react-router-dom";
import {TagFlow} from "../../../components/TagFlow";
import {
    addLocationTag,
    fetchLocationsCollection,
    fetchLocationsLore,
    removeLocationTag, saveLocation
} from "../../../feature/project/locationsSlice";
import LayerList from "../../common/LayerList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlay, faPlus, faStop} from "@fortawesome/free-solid-svg-icons";
import {deleteLayer, saveLayer, saveLocationLayer} from "../../../feature/project/layerSlice";
import {Starfield} from "../../../components/Starfiled";
import {LayerEditModal} from "./LayerEditModal";
import {ElementProperties} from "./ElementProperties";
import {ElementsCollection} from "../../common/ElementsCollection";
import {ElementInfo} from "./ElementInfo";
import {AnimatableElement} from "./AnimatableElement";
import {EditElement} from "./EditElement";

const {confirm} = Modal;

export const LocationEditModal = (
    props: {
        location: Location,
        onSave: (location: Location) => void,
        onCancel: () => void
    }
) => {
    const dispatch = useAppDispatch()
    const {projectId, shotId} = useParams()
    const [title, setTitle] = React.useState(props.location.title)
    const [tags, setTags] = React.useState<Array<Tag>>(props.location.tags || [])
    const [showSelectLocation, setShowSelectLocation] = React.useState(false)

    const [hasChanges, setHasChanges] = React.useState(false)

    useEffect(() => {
        setHasChanges(title !== props.location.title)
    }, [title])

    useEffect(() => {
        setTitle(props.location.title)
        setTags(props.location.tags || [])
    }, [props.location, props.location.tags])

    useEffect(() => {
        if (!props.location.id) {
            setShowSelectLocation(true)
        }
    }, [props.location.id])

    return (
        <>
            <Modal
                open={true}
                title={props.location.id ? 'Edit Location' : 'Add Location'}
                width={1000}
                okText={'Save'}
                cancelText={'Close'}
                onCancel={props.onCancel}
                okButtonProps={{disabled: !hasChanges}}
                onOk={() => props.onSave({
                    ...props.location,
                    title: title,
                    tags: tags
                })}
            >
                {showSelectLocation ?
                    <LocationSelectView onLocationSelected={(location) => {
                        if (projectId && shotId) {
                            const loc = {
                                ...location,
                                id: undefined,
                                shot: {
                                    id: parseInt(shotId)
                                },
                                layers: location?.layers?.map(layer => {
                                    return {
                                        ...layer,
                                        id: undefined,
                                        animations: layer.animations?.map(anim => {
                                            return {...anim, id: undefined}
                                        })
                                    }
                                })
                            }
                            dispatch(saveLocation({projectId: parseInt(projectId), location: loc as Location}))
                            setShowSelectLocation(false)
                        }
                    }}/>
                    :
                    <LocationEditView
                        location={props.location}
                        title={title}
                        tags={tags}
                        onTitleChanged={(value) => setTitle(value)}
                        onTagsChanged={(value) => setTags(value)}
                    />}
            </Modal>
        </>
    )
}

const LocationSelectView = (props: { onLocationSelected: (location: Location) => void }) => {
    const dispatch = useAppDispatch()
    const {projectId} = useParams()
    const {storyId} = useParams()
    const locationsCollection = useAppSelector(state => state.locations.collection)
    const locationsLore = useAppSelector(state => state.locations.lore)
    const status = useAppSelector(state => state.locations.status)

    useEffect(() => {
        if (status === Status.Idle && storyId && projectId) {
            dispatch(fetchLocationsCollection({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
            dispatch(fetchLocationsLore({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
        }
    }, [status, storyId, projectId])

    return (
        <div style={{height: 400}}>
            <Tabs
                defaultActiveKey="1"
                items={[
                    {
                        label: `Collection`,
                        key: "1",
                        children: <ElementsCollection
                            animated={true}
                            key={"1"} elements={locationsCollection || []}
                            onSelect={(element) => {
                                props.onLocationSelected(element as Location)
                            }}/>,
                    },
                    {
                        label: `Lore`,
                        key: "2",
                        children: <ElementsCollection
                            animated={false}
                            key={"2"}
                            elements={locationsLore || []}
                            onSelect={(element: any) => {
                                props.onLocationSelected(
                                    {
                                        title: (element as Location).title,
                                        component: element?.component
                                    }
                                )
                            }}/>,
                    },
                ]}
            />
        </div>
    )
}

const LocationEditView = (props: {
    location: Location,
    title?: string,
    tags: Array<Tag>,
    onTitleChanged: (value: string) => void,
    onTagsChanged: (value: Array<Tag>) => void
}) => {
    const status = useAppSelector(state => state.locations.status)
    const dispatch = useAppDispatch()
    const {projectId, storyId} = useParams()
    const [scale, setScale] = React.useState(1)
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const allTags = useAppSelector(state => state.projects.tags)
    const [layer, setLayer] = React.useState<Combined | undefined>(undefined)
    const [showEditLayerModal, setShowEditLayerModal] = React.useState<Layer | undefined>(undefined)
    const [play, setPlay] = React.useState(ShotState.Idle)
    const [info, setInfo] = React.useState({x: 0, y: 0, z: 0, scale: 1})
    const [played, setPlayed] = useState<Set<number>>(new Set())

    useEffect(() => {
        if (layer) {
            const selectedLayer: Layer | undefined = props.location.layers?.find(lay => lay.id === layer?.id)
            setLayer({...selectedLayer, type: 'Layer'})
        }
        if (showEditLayerModal) {
            const editedLayer: Layer | undefined = props.location.layers?.find(lay => lay.id === showEditLayerModal?.id)
            setShowEditLayerModal({...editedLayer, type: 'Layer'})
        }
    }, [props.location])

    useEffect(() => {
        if (played.size === ((props.location.layers?.length ?? 0) + 1) && play || 0 === ((props.location.layers?.length ?? 0) + 1) && play) {
            setPlayed(new Set())
            setPlay(ShotState.Idle)
        }
    }, [played, play]);

    useEffect(() => {
        setInfo({x: layer?.x || 0, y: layer?.y || 0, z: layer?.z || 0, scale: layer?.scale || 1})
    }, [layer])

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height, props.location])


    useEffect(() => {
        if (status === Status.Idle && projectId) {
            if (props.location.id) {
                dispatch(fetchTags({projectId: parseInt(projectId)}))
            }
        }
    }, [status, dispatch])


    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    const showConfirmLayerDeletion = (item: Layer) => {
        confirm({
            title: 'Delete Layer',
            content: 'Are you sure you want to delete this layer? It will also delete all the animations associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteLayer({projectId: parseInt(projectId), layerId: item.id}))
                }
            },
        });
    }

    return (
        <>
            <Flex gap={16}>
                <Flex align={'center'} style={{
                    minWidth: 400,
                    height: 400,
                    borderRadius: 6,
                    clipPath: 'border-box',
                    position: "relative",
                    background: '#eeeeee'
                }}>
                    <div
                        ref={elementRef}
                        style={{
                            position: 'relative',
                            width: '100%',
                            height: 'calc(100% / 1.77)'
                        }}>
                        {props.location.layers && props.location.layers.length > 0 ?
                            <div style={{
                                width: 1920,
                                height: 1080,
                                overflow: 'hidden',
                                background: '#000',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                scale: `${scale}`,
                                transformOrigin: 'top left'
                            }}>
                                <AnimatableElement
                                    element={props.location}
                                    play={play !== ShotState.Idle}
                                    scale={1}
                                    animations={[]}
                                    onPlayEnd={(id: number) => {

                                    }}>

                                    {props.location.layers && props.location.layers.length > 0 ?
                                        props.location.layers.map(lay => {
                                            return <EditElement
                                                key={lay.id}
                                                prefix={"object-edit"}
                                                element={lay}
                                                play={play !== ShotState.Idle}
                                                onPlayEnd={() => {
                                                    if (play) {
                                                        setPlayed(prev => new Set(prev).add(lay.id ?? 0))
                                                    }
                                                }}
                                                selectedElement={layer}
                                                scale={scale}
                                                onElementSelected={(element) => {
                                                    setLayer(element)
                                                }}
                                                onMove={(info: { x: number, y: number, z: number, scale: number }) =>
                                                    setInfo(info)
                                                }
                                            />
                                        }) : ''
                                    }
                                </AnimatableElement>
                            </div> :
                            <div style={{
                                width: '100%',
                                height: 'calc(100% / 1.77)',
                                background: "#000000",
                                position: "relative"
                            }}>
                                <Starfield
                                    id={"location-edit-starfield"}
                                    starCount={5000}
                                    starColor={[255, 255, 255]}
                                    speedFactor={0.1}
                                    scale={scale}
                                    backgroundColor="black"/></div>
                        }
                    </div>
                    <Button icon={<FontAwesomeIcon icon={play !== ShotState.Idle ? faStop : faPlay}/>}
                            onClick={() => setPlay(play === ShotState.Playing ? ShotState.Idle : ShotState.Playing)}
                            style={{
                                lineHeight: '12px',
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 10000,
                                width: 38
                            }}
                            disabled={!props.location.layers || props.location.layers.reduce((acc, curr) => acc + (curr.animations?.length || 0), 0) === 0}
                    />
                    {layer ?
                        <>
                            <ElementProperties
                                style={{
                                    zIndex: 100000,
                                    position: 'absolute',
                                    right: 10,
                                    top: 52,
                                    background: '#fff',
                                    borderRadius: 6,
                                    padding: 2,
                                    bottom: 10
                                }}
                                element={layer}
                                onScaleChanged={(value) => {
                                    setLayer(
                                        {
                                            ...layer,
                                            scale: value
                                        } as Transformable
                                    )
                                    setInfo({...info, scale: value})
                                }}
                                onTransformed={(element) => {
                                    if (projectId && element && storyId) {
                                        dispatch(saveLayer({
                                            projectId: parseInt(projectId),
                                            storyId: parseInt(storyId),
                                            layer: element as Layer
                                        }))
                                    }
                                }}
                                onAlignmentChanged={() => {
                                }}
                            />
                            <ElementInfo element={info}/>
                        </> : ''}
                </Flex>
                <Space direction={'vertical'} style={{width: '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                       size={16}>
                    <div>
                        <Typography style={{fontWeight: 700}}>Title</Typography>
                        <Input value={props.title} onChange={(e) => props.onTitleChanged(e.target.value)}/>
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <TagFlow allTags={allTags}
                                 tags={props.tags}
                                 onTagAdded={(tag) => {
                                     props.onTagsChanged([...props.tags, tag])
                                     if (props.location.id && projectId) {
                                         dispatch(addLocationTag({
                                             projectId: parseInt(projectId),
                                             locationId: props.location.id,
                                             tag: tag
                                         }))
                                     }
                                 }}
                                 onTagDeleted={(tag) => {
                                     if (tag.id && props.location.id && projectId) {
                                         dispatch(removeLocationTag({
                                             projectId: parseInt(projectId),
                                             locationId: props.location.id,
                                             tagId: tag.id
                                         }))
                                     }
                                 }}
                        />
                    </div>
                    <div>
                        <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                            <Typography style={{fontWeight: 700}}>Layers</Typography>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                onClick={() =>
                                    setShowEditLayerModal({})
                                }
                            />
                        </Flex>

                        <LayerList
                            is={(item) => layer?.id === item.id}
                            data={props.location.layers?.map(lay => {
                                return layer !== undefined && lay.id === layer.id ? {
                                    ...layer,
                                    layers: [layer],
                                    type: 'Layer'
                                } as Combined : {...lay, layers: [lay], type: 'Layer'} as Combined
                            })}
                            clickable={true}
                            itemStyle={{padding: 0}}
                            title={(item, index) => <Flex align={'center'} gap={10}>
                                <img src={(item as Layer).source}
                                     style={{
                                         width: 50,
                                         height: 50,
                                         borderRadius: '5px',
                                         objectFit: 'contain',
                                         background: '#eeeeee'
                                     }}
                                />
                                {`Layer ${index + 1}`}
                            </Flex>}
                            onItemClick={(item) => {
                                setLayer(item)
                            }}
                            onDeleteClick={(item) => {
                                showConfirmLayerDeletion(item as Layer)
                            }}
                            onEditClick={(item) => {
                                setLayer(item)
                                setShowEditLayerModal(item as Layer)
                            }}
                        />
                    </div>
                </Space>
            </Flex>
            {showEditLayerModal ?
                <LayerEditModal
                    componentId={props.location?.component?.id}
                    key={showEditLayerModal?.id || '' + showEditLayerModal?.animations?.map(an => an.id)?.join('')}
                    layer={showEditLayerModal}
                    onCancel={() => setShowEditLayerModal(undefined)}
                    onSave={(layer) => {
                        if (projectId && layer && storyId && props.location.id) {
                            if (layer.id) {
                                dispatch(saveLayer({
                                    projectId: parseInt(projectId),
                                    storyId: parseInt(storyId),
                                    layer: layer
                                }))
                            } else {
                                dispatch(saveLocationLayer({
                                    projectId: parseInt(projectId),
                                    locationId: props.location.id,
                                    storyId: parseInt(storyId),
                                    layer: layer
                                }))
                            }
                        }
                        setShowEditLayerModal(undefined)
                    }}
                /> : ''}
        </>
    )
}